import { Mutex } from 'async-mutex'
import * as R from 'ramda'
import {
  DBFind,
  DBFindOne,
  DBInsert,
  DBUpdateOne,
  DBRemoveMany,
} from '@weblite/persist'

interface StoredPatch<G> {
  id: string
  nextKey: number
  events: Record<number, G>
}

export function eventBasedSynchronizer<
  T extends Record<string, unknown>,
  P extends boolean
>(collectionName: string, options: { merge: P }) {
  const mutex = new Mutex()

  async function set(id: string, fields: T) {
    const storedKey = await mutex.runExclusive(async () => {
      const { nextKey } = (await DBFindOne(collectionName, id)) || {}
      if (nextKey) {
        await DBUpdateOne(collectionName, id, {
          $inc: { nextKey: 1 },
          $set: { [`events.${nextKey}`]: fields },
        })
      } else {
        await DBInsert(collectionName, {
          id,
          nextKey: 2,
          events: { '1': fields },
        })
      }

      return nextKey || 1
    })

    // TODO: check db transactions, should run exclusively
    return async function unset() {
      await DBUpdateOne(collectionName, id, {
        $unset: { [`events.${storedKey}`]: 1 },
      })
    }
  }

  async function del(items: StoredPatch<T>[]) {
    await Promise.all(
      items.map(item =>
        DBUpdateOne(collectionName, item.id, {
          $unset: R.fromPairs(
            R.keys(item.events).map(key => [`events.${key}`, 1]),
          ),
        }),
      ),
    )
  }

  async function getAll() {
    const release = await mutex.acquire()
    try {
      const pruneList: string[] = []
      // NOTE: empty entities has been removed in the above function

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const itemSyncs: StoredPatch<T>[] = await DBFind(collectionName)
      const items = itemSyncs
        .map(item => {
          if (R.isEmpty(item.events)) {
            pruneList.push(item.id)
            return null
          }
          const patches = R.reduce(
            (acc, key: number) => [...acc, item.events[key]],
            [] as T[],
            R.sort((a, b) => +a - +b, R.keys(item.events)),
          )
          return { id: item.id, patches }
        })
        .filter(Boolean)

      const finalItems = (options.merge
        ? items.map(i => ({ id: i.id, patch: R.mergeAll(i.patches) }))
        : items) as P extends true ? { id: string; patch: T }[] : typeof items

      if (pruneList.length) await DBRemoveMany(collectionName, pruneList)

      return { items: finalItems, del: () => del(itemSyncs), release }
    } catch (err) {
      release()
      throw err
    }
  }

  function getIds(): Promise<string[]> {
    return DBFind(collectionName, undefined, { keyOnly: true })
  }

  return { set, getAll, getIds }
}
